import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../../context/AuthContext';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface TemplateDetails {
  id: number;
  templateName: string | null;
  language: string | null;
  formName: string | null;
  caregiverType: string | null;
  createdAt: string;
}

export default function TemplateDetail() {
  const { templateId } = useParams<{ templateId: string }>();
  const { authFetch } = useAuth();
  const navigate = useNavigate();

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [details, setDetails] = useState<TemplateDetails | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [newPdfFile, setNewPdfFile] = useState<File | null>(null);

  // Inline editing state
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const fetchPdf = async () => {
    try {
      const res = await authFetch(`/api/templates/${templateId}`);
      if (!res.ok) return;
      const blob = await res.blob();
      setPdfUrl(URL.createObjectURL(blob));
    } catch (err) {
      console.error('Error fetching template PDF:', err);
    }
  };

  const fetchDetails = async () => {
    try {
      const res = await authFetch(`/api/templates/${templateId}/details`);
      if (res.ok) setDetails(await res.json());
    } catch (err) {
      console.error('Error fetching template details:', err);
    }
  };

  useEffect(() => {
    fetchPdf();
    fetchDetails();
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [templateId]);

  const startEditing = (field: string, currentValue: string | null) => {
    setEditingField(field);
    setEditValue(currentValue || '');
  };

  const saveField = async () => {
    if (!editingField) return;
    try {
      const endpoint =
        editingField === 'templateName'
          ? `/api/templates/${templateId}/rename`
          : `/api/templates/${templateId}/fields`;

      const body =
        editingField === 'templateName'
          ? { templateName: editValue }
          : { [editingField]: editValue };

      await authFetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      setDetails((prev) => (prev ? { ...prev, [editingField]: editValue } : prev));
      setEditingField(null);
      setEditValue('');
    } catch (err) {
      console.error(`Error updating ${editingField}:`, err);
    }
  };

  const handleReplace = async () => {
    if (!newPdfFile) {
      alert('Please select a PDF file.');
      return;
    }
    const formData = new FormData();
    formData.append('pdfFile', newPdfFile);

    try {
      await authFetch(`/api/templates/${templateId}/replace`, {
        method: 'PUT',
        body: formData,
      });
      setShowReplaceDialog(false);
      setNewPdfFile(null);
      fetchPdf();
      fetchDetails();
    } catch (err) {
      console.error('Error replacing template:', err);
    }
  };

  const handleDownload = () => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = `template-${templateId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

  const EditableField = ({ label, field, value }: { label: string; field: string; value: string | null }) => (
    <Box
      onDoubleClick={() => startEditing(field, value)}
      sx={{ mb: 1, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
    >
      {editingField === field ? (
        <TextField
          autoFocus
          fullWidth
          size="small"
          label={label}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={saveField}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveField();
            if (e.key === 'Escape') setEditingField(null);
          }}
        />
      ) : (
        <Typography variant="body1">
          <strong>{label}:</strong> {value || 'N/A'}
        </Typography>
      )}
    </Box>
  );

  if (!pdfUrl || !details) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 3, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 2, bgcolor: 'grey.800', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Box
              onDoubleClick={() => startEditing('templateName', details.templateName)}
              sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
            >
              {editingField === 'templateName' ? (
                <TextField
                  autoFocus
                  fullWidth
                  variant="outlined"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={saveField}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveField();
                    if (e.key === 'Escape') setEditingField(null);
                  }}
                  sx={{
                    mb: 1,
                    input: { color: 'white' },
                    '& .MuiOutlinedInput-root fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                  }}
                />
              ) : (
                <Typography variant="h5" gutterBottom>
                  {details.templateName || 'Untitled Template'}
                </Typography>
              )}
            </Box>

            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Created: {formatDate(details.createdAt)}
            </Typography>

            <EditableField label="Language" field="language" value={details.language} />
            <EditableField label="Form Name" field="formName" value={details.formName} />
            <EditableField label="Caregiver Type" field="caregiverType" value={details.caregiverType} />

            <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
              Double-click any field to edit
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
            <Button variant="contained" color="secondary" onClick={() => setShowReplaceDialog(true)}>
              Replace PDF
            </Button>
            <Button variant="contained" onClick={handleDownload}>
              Download PDF
            </Button>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/templates')}
              sx={{ mt: 2, color: 'white', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white', borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              Back to Templates
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Replace dialog */}
      <Dialog open={showReplaceDialog} onClose={() => setShowReplaceDialog(false)}>
        <DialogTitle>Replace PDF Template</DialogTitle>
        <DialogContent>
          <input
            style={{ marginTop: 16 }}
            type="file"
            accept=".pdf"
            onChange={(e) => setNewPdfFile(e.target.files?.[0] || null)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReplaceDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleReplace}>Upload</Button>
        </DialogActions>
      </Dialog>

      {/* PDF Viewer */}
      <Paper elevation={3} sx={{ p: 2, bgcolor: 'grey.200', display: 'flex', justifyContent: 'center' }}>
        <Document file={pdfUrl} onLoadSuccess={({ numPages: n }) => setNumPages(n)}>
          {Array.from(new Array(numPages), (_, i) => (
            <Box
              key={`page-${i + 1}`}
              sx={{
                mb: 2,
                bgcolor: 'white',
                borderRadius: 1,
                boxShadow: 2,
                p: 2,
                display: 'flex',
                justifyContent: 'center',
                '& canvas': { maxWidth: '100%', height: 'auto !important' },
              }}
            >
              <Page pageNumber={i + 1} />
            </Box>
          ))}
        </Document>
      </Paper>
    </Container>
  );
}
